import { FC, ReactNode } from 'react'

import { Box, Card, Divider, Typography } from '@mui/material'

interface SectionCardProps {
  title: ReactNode
  /** Slot rendered centered in the header row (e.g. a legend). */
  headerCenter?: ReactNode
  /** Slot rendered on the right side of the header row (e.g. action button). */
  headerAction?: ReactNode
  /**
   * When true, the body is rendered without padding — for example when the body
   * is a `GRTable` that already owns its own padding. Defaults to `false`.
   */
  disableBodyPadding?: boolean
  children?: ReactNode
}

/**
 * Outlined card with a bold title row and a divider, used throughout ua-insights
 * to group dialog content into consistent sections.
 */
export const SectionCard: FC<SectionCardProps> = ({ title, headerCenter, headerAction, disableBodyPadding = false, children }) => {
  return (
    <Card
      variant="outlined"
      sx={
        disableBodyPadding
          ? {
              // When the body is a table, drop the bottom border of the last row's
              // cells so it does not visually double up with the Card's own border.
              '& .MuiTableRow-root:last-of-type .MuiTableCell-body': { borderBottom: 0 },
            }
          : undefined
      }
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1 }}>
        {/* Left/right wrappers flex equally so the centered slot stays truly
            centered regardless of the title and action widths. */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {typeof title === 'string' ? (
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              {title}
            </Typography>
          ) : (
            title
          )}
        </Box>
        {headerCenter && <Box sx={{ display: 'flex', justifyContent: 'center', flexShrink: 0 }}>{headerCenter}</Box>}
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>{headerAction}</Box>
      </Box>
      <Divider />
      {disableBodyPadding ? children : <Box sx={{ px: 2, py: 1.5 }}>{children}</Box>}
    </Card>
  )
}
